import { Button } from 'app/components/BasicButtons/Button';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { CalendarButton } from 'app/components/CalendarButton';
import { MenuItemLinkProps } from 'app/components/Menu/MenuItemLink';
import {
  selectAppSettings,
  selectAuthenticatedUser,
  selectglobalSettings,
} from 'app/slice/selectors';
import { translations } from 'locales/translations';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IsModuleEnabled } from 'types/AppSettings';
import { KnownModules } from 'types/KnownModules';
import { toRootedURL } from 'utils/url-utils';
import { IAsset, mapToAssetDto } from '../IAsset';
import { alertsEnabled } from '..';
import { getAssetEditLink } from '../getAssetEditLink';
import { isBookable } from './Utils';
import { RenderTypes } from 'app/pages/Actions/GlobalSelectedActions/ValidToRender/renderTypes';
import { useAssetsPermissions } from 'app/pages/Actions/GlobalSelectedActions/ValidToRender/useAssetsPermissionsUtils';
import { ServiceType } from 'enums/ServiceTypes';
import { AvailabilityTypes } from 'api/odata/generated/enums/AvailabilityTypes';
import { PrintSelectedAssetLabelsButton } from 'app/components/LabelPrint/PrintSelectedAssetLabelsButton';
import { IMyAssetsRow } from 'app/pages/MyAssetsPage/IMyAssetsRow';
import { ExportQRCodesButton } from 'app/components/QrCodeExport';
import { Roles } from 'api/odata/generated/enums/Roles';

export interface TopAssetActionsDropDownProps {
  data: IAsset;
  buttonsData: IAsset;
}

const ButtonOrLink = (props: MenuItemLinkProps) => {
  const { icon, target, text, disabled, ...rest } = props;
  return (
    <Button
      startIcon={!icon ? undefined : <Icon icon={icon} />}
      size={'small'}
      variant="white"
      component={'a'}
      disabled={disabled}
      target={!!target ? '_blank' : '_self'}
      {...rest}
    >
      {text}
    </Button>
  );
};
export const useTopAssetActionsDropDown = ({
  isMismatchCore,
  isAdmin,
}: {
  isMismatchCore?: boolean;
  isAdmin: boolean;
}) => {
  const { t } = useTranslation();
  const appSettings = useSelector(selectAppSettings);
  const globalSettings = useSelector(selectglobalSettings);
  const authenticatedUser = useSelector(selectAuthenticatedUser);
  const roles = authenticatedUser?.Roles;
  const canAddQuiz = React.useCallback((): boolean => {
    return (
      authenticatedUser !== undefined &&
      authenticatedUser.Roles.indexOf(Roles.GroupAdministrators) >= 0
    );
  }, [authenticatedUser]);
  const { allowedToView } = useAssetsPermissions();

  const getMenuItems = React.useCallback(
    ({ data, buttonsData }: TopAssetActionsDropDownProps) => {
      const rows = [
        {
          Id: data.Id,
          Name: data.Name,
          AssetCatId: data?.AssetCat?.Id,
          AvailabilityTypeId: AvailabilityTypes[data.AvailabilityType.Id],
          AvailabilityTypeName: data.AvailabilityType.Name,
          ServiceGroupId: data.ServiceGroup?.Id,
          ServiceGroupName: data.ServiceGroup?.Name,
          ServiceId: data?.ServiceId,
          ServiceTypeId:
            data?.ServiceType === null
              ? null
              : (ServiceType[data.ServiceType] as number) ?? null,
          ServiceGroupAssetId: data?.ServiceGroup?.AssetId,
        },
      ];
      const NotificationIsAvailable = [
        'FreelyBookableBillable',
        'ApprovalRequiredBillable',
        'FreelyBookable',
        'BookableWApproval',
        'TrainingProgram',
        'LoanableEquipment',
        'LectureRoom',
      ].includes(data.AvailabilityType.Id);
      let items: React.ReactNode[] = [];
      if (
        isAdmin &&
        [
          'NotAvailable',
          'AvailableNotBookable',
          'FreelyBookableBillable',
          'ApprovalRequiredBillable',
          'FreelyBookable',
          'BookableWApproval',
          'TrainingProgram',
          'LoanableEquipment',
          'LectureRoom',
          'ServiceRelated',
          'RequestRelated',
          'ServiceGroup',
          'SuperGroup',
        ].includes(data.AvailabilityType.Id)
      ) {
        const editLink = getAssetEditLink(
          data.AvailabilityType.Id,
          data.Id,
          data.ServiceId ?? undefined,
          data.ServiceGroup?.Id ?? undefined,
          data.ServiceGroup?.DivisionId ?? undefined,
        );
        if (editLink !== undefined) {
          items.push(
            <ButtonOrLink
              key="edit-service"
              href={editLink}
              title={t(translations.EditSettings)}
              text={t(translations.Settings)}
              icon="pencil-alt"
              target={'_blank'}
            />,
          );
        }
      }
      /*items.push(
        <ButtonOrLink
          key="asset-details"
          href={toRootedURL('/Assets/Details?id=' + data.Id)}
          title={t(translations.AssetDetails)}
          text={t(translations.AssetDetails)}
          target="_blank"
          icon="file"
        />,
      );*/
      if (
        (buttonsData.ReservationEnabled &&
          roles?.includes(Roles.Administrators)) ||
        roles?.includes(Roles.LabTech)
      ) {
        items.push(
          <ButtonOrLink
            key="new-session"
            href={toRootedURL(
              '/TrainingSession/AddNew.aspx?eqid=' + data.ServiceId,
            )}
            title={t(translations.menu_AddNewTrainingSessions)}
            disabled={isMismatchCore}
            icon="plus"
            target="_blank"
            text={t(translations.NewTrainingSession)}
          />,
        );
      }
      if (
        buttonsData.ReservationEnabled &&
        data.ServiceType === 'Online' &&
        isBookable(data.AvailabilityType?.Id)
      ) {
        items.push(
          <CalendarButton
            key="calendar-open"
            equipments={data.ServiceId !== null ? [data.ServiceId] : undefined}
            openToBook={true}
            disabled={isMismatchCore}
            title={
              data.Allowed_ByTraining === false
                ? t(translations.UntrainedToolTip)
                : 'Calendar'
            }
            startIcon={<Icon icon="calendar-alt" />}
            variant="white"
            component={'a'}
            size={'small'}
          >
            {t(translations.Calendar)}
          </CalendarButton>,
        );
      }
      if (
        buttonsData.ReservationEnabled &&
        isBookable(data.AvailabilityType?.Id)
      ) {
        items.push(
          <CalendarButton
            key="timeline-open"
            equipments={[data.ServiceId ?? undefined]}
            openToBook={true}
            viewType="timeline"
            disabled={isMismatchCore}
            title={t(translations.Timeline)}
            variant="white"
            component={'a'}
            startIcon={<Icon icon="stream" />}
            size={'small'}
          >
            {t(translations.Timeline)}
          </CalendarButton>,
        );
        if (
          allowedToView(
            ((rows as unknown) as IMyAssetsRow[]).map(mapToAssetDto),
            RenderTypes.ExportQRCodes,
          )
        ) {
          items.push(
            <ExportQRCodesButton url="/api/QrCodes/assets" rows={rows} />,
          );
        }
        if (
          allowedToView(
            ((rows as unknown) as IMyAssetsRow[]).map(mapToAssetDto),
            RenderTypes.PrintAssetLabels,
          )
        ) {
          items.push(
            <PrintSelectedAssetLabelsButton
              key="printlabels"
              rows={rows}
              title={t(translations.PrintLabels)}
            />,
          );
        }
        if (isBookable(data.AvailabilityType?.Id)) {
          items.push(
            <ButtonOrLink
              key="res-history"
              href={toRootedURL('/reservations?eid=' + data.ServiceId)}
              title={t(translations.menu_ReservationsHistory)}
              icon="calendar-alt"
              text={t(translations.menu_ReservationsHistory)}
              disabled={isMismatchCore}
            />,
          );
        }
      }
      if (buttonsData.UsageEnabled) {
        items.push(
          <ButtonOrLink
            key="usage-history"
            href={toRootedURL('/usagehistory.aspx?sid=' + data.ServiceId)}
            title={t(translations.UsageHistory)}
            icon="ruler-triangle"
            text={t(translations.UsageHistory)}
            disabled={isMismatchCore}
          />,
        );
      }
      if (data.ServiceType === 'Offline' && data.AllowToUser) {
        items.push(
          <ButtonOrLink
            key="offline-services"
            href={toRootedURL('/otherservices?sid=' + data.ServiceId)}
            title={t(translations.OfflineService)}
            icon="th-list"
            text={t(translations.OfflineService)}
            disabled={isMismatchCore}
          />,
        );
      }
      if (
        data.ServiceType === 'Online' &&
        IsModuleEnabled(appSettings, KnownModules.Quizzes) &&
        canAddQuiz()
      ) {
        items.push(
          <ButtonOrLink
            key="quizzes"
            href={toRootedURL('/quizzes?eid=' + data.ServiceId)}
            title={t(translations.TrainingQuiz)}
            icon="plus"
            text={t(translations.TrainingQuiz)}
            disabled={isMismatchCore}
          />,
        );
      }
      if (alertsEnabled(data.AvailabilityType.Id)) {
        items.push(
          <ButtonOrLink
            key="workorders"
            href={toRootedURL(`/workorders?aid=${data.Id}`)}
            title={t(translations.Alerts)}
            icon="comment-alt-dots"
            text={t(translations.AlertsIcon)}
            disabled={isMismatchCore}
          />,
        );
      }
      if (buttonsData.PlanedEventsEnabled) {
        items.push(
          <ButtonOrLink
            key="incident-track"
            href={toRootedURL(
              '/Alerts/IncidentAssetsTracking.aspx?assetIds=' + data.Id,
            )}
            title={t(translations.ScheduledEvents)}
            icon="clipboard-list-check"
            text={t(translations.ScheduledEvents)}
            target={'_blank'}
            disabled={isMismatchCore}
          />,
        );
      }
      if (data.ServiceType === 'Offline' && data.AllowToUser) {
        items.push(
          <ButtonOrLink
            key="request-otherservice"
            href={toRootedURL('/OfflineServices.aspx?sid=' + data.ServiceId)}
            title={t(translations.RequestAnOtherService)}
            icon="history"
            text={t(translations.RequestAnOtherService)}
            disabled={isMismatchCore}
          />,
        );
      }
      if (
        data.ServiceType === 'Sample' &&
        data.CanBeCreatedFromLinkOnly === false
      ) {
        items.push(
          <ButtonOrLink
            key="workflow-step"
            href={toRootedURL('/WorkflowStep.aspx?ostid=' + data.ServiceId)}
            title={t(translations.CreateNewSampleRequest)}
            icon="bolt"
            target={'_blank'}
            text={t(translations.CreateNewSampleRequest)}
            disabled={isMismatchCore}
          />,
        );
        /*items.push(
          <ButtonOrLink
            key="service-request"
            href={toRootedURL('/ServiceRequests.aspx?OfflineServiceTypeId=' + data.ServiceId)}
            title={t(translations.SampleRequests)}
            icon="history"
            text={t(translations.SampleRequests)}
            disabled={isMismatchCore}
          />,
        );*/
      }
      if (
        isBookable(data.AvailabilityType?.Id) &&
        data.ServiceType === 'Online' &&
        buttonsData.ReservationEnabled &&
        (IsModuleEnabled(appSettings, KnownModules.ResCancelNotifications) ||
          IsModuleEnabled(appSettings, KnownModules.ReservationNotification)) &&
        NotificationIsAvailable
      ) {
        items.push(
          <ButtonOrLink
            key="notifications"
            href={toRootedURL(
              `/UserNotifications.aspx?exist=${
                buttonsData.NotifyExists ? 'yes' : 'no'
              }&EquipId=${data.ServiceId}&prev=home`,
            )}
            title={t(
              buttonsData.NotifyExists
                ? translations.NotifyToolTip
                : translations.NoNotifyToolTip,
            )}
            icon="bell"
            target={'_blank'}
            text={t(
              buttonsData.NotifyExists
                ? translations.EditRemainders
                : translations.AddRemainders,
            )}
          />,
        );
      }
      if (
        buttonsData.ShowFeed &&
        globalSettings.GetBoolean('CalendarPublicIntegrationFeed')
      ) {
        items.push(
          <ButtonOrLink
            key="calendar-feed"
            title={t(translations.CalendarPublicIntegrationFeed_Icon)}
            href={toRootedURL(
              '/api/ICalController/import?EquipmentName=' + data.Name,
            )}
            icon="calendar-plus"
            text={t(translations.OutlookGoogle)}
          />,
        );
      }
      return items;
    },
    [
      allowedToView,
      appSettings,
      canAddQuiz,
      globalSettings,
      isAdmin,
      isMismatchCore,
      roles,
      t,
    ],
  );
  return getMenuItems;
};
